import { useState, useEffect, useCallback } from 'react'
import { DonutChart, type DonutChartData } from '@/components/charts/DonutChart'
import { BarChart, type BarChartData } from '@/components/charts/BarChart'
import { formatCurrency } from '@/components/charts/chartUtils'

// Type labels for display
const assetTypeLabels: Record<string, string> = {
  bank_checking: 'Checking',
  bank_savings: 'Savings',
  investment_brokerage: 'Brokerage',
  investment_401k: '401(k)',
  investment_ira: 'IRA',
  property: 'Property',
  vehicle: 'Vehicle',
  other: 'Other'
}

const debtTypeLabels: Record<string, string> = {
  mortgage: 'Mortgage',
  auto_loan: 'Auto Loan',
  credit_card: 'Credit Card',
  student_loan: 'Student Loan',
  personal_loan: 'Personal Loan',
  other: 'Other'
}

interface Asset {
  id: number
  name: string
  type: string
  current_value: number
  appreciation_rate: number
  family_member: string
  ticker: string | null
  gain_loss: number | null
  gain_loss_percent: number | null
}

interface Debt {
  id: number
  name: string
  type: string
  principal: number
  interest_rate: number
  monthly_payment: number
  family_member: string
  months_remaining: number | null
  payoff_date: string | null
}

interface NetWorthData {
  summary: {
    total_assets: number
    total_liabilities: number
    net_worth: number
    by_family_member: Record<string, { assets: number; liabilities: number; net_worth: number }>
    by_asset_type: Record<string, number>
    by_debt_type: Record<string, number>
    projections: {
      one_year: number
      five_year: number
      ten_year: number
    }
  }
  assets: Asset[]
  debts: Debt[]
}

type TabType = 'overview' | 'assets' | 'liabilities'

export function NetWorth() {
  const [data, setData] = useState<NetWorthData | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [selectedMember, setSelectedMember] = useState<string | null>(null)

  const fetchNetWorth = useCallback(async () => {
    try {
      const response = await fetch('/api/net-worth')
      if (response.ok) {
        const result = await response.json() as NetWorthData
        setData(result)
      }
    } catch (error) {
      console.error('Error fetching net worth:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNetWorth()
  }, [fetchNetWorth])

  // Transform data for charts
  const assetTypeData: DonutChartData[] = data
    ? Object.entries(data.summary.by_asset_type).map(([type, value]) => ({
        name: assetTypeLabels[type] || type,
        value
      }))
    : []

  const debtTypeData: DonutChartData[] = data
    ? Object.entries(data.summary.by_debt_type).map(([type, value]) => ({
        name: debtTypeLabels[type] || type,
        value
      }))
    : []

  const familyMemberData: BarChartData[] = data
    ? Object.entries(data.summary.by_family_member).map(([name, values]) => ({
        name,
        value: values.assets,
        value2: values.liabilities
      }))
    : []

  // Filter assets/debts by selected member
  const filteredAssets = selectedMember
    ? data?.assets.filter(a => a.family_member === selectedMember) || []
    : data?.assets || []

  const filteredDebts = selectedMember
    ? data?.debts.filter(d => d.family_member === selectedMember) || []
    : data?.debts || []

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'assets', label: 'Assets' },
    { id: 'liabilities', label: 'Liabilities' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-forest-dark">Net Worth</h1>
          <p className="text-sage mt-1">Track your assets and liabilities</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedMember(null)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              !selectedMember
                ? 'bg-forest text-white'
                : 'bg-cream text-charcoal hover:bg-stone/20'
            }`}
          >
            All
          </button>
          {data && Object.keys(data.summary.by_family_member).map(member => (
            <button
              key={member}
              onClick={() => setSelectedMember(member)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedMember === member
                  ? 'bg-forest text-white'
                  : 'bg-cream text-charcoal hover:bg-stone/20'
              }`}
            >
              {member}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <p className="text-sm text-stone">Total Assets</p>
          <p className="text-2xl font-bold text-success tabular-nums">
            {loading ? '...' : formatCurrency(
              selectedMember
                ? data?.summary.by_family_member[selectedMember]?.assets || 0
                : data?.summary.total_assets || 0
            )}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <p className="text-sm text-stone">Total Liabilities</p>
          <p className="text-2xl font-bold text-danger tabular-nums">
            {loading ? '...' : formatCurrency(
              selectedMember
                ? data?.summary.by_family_member[selectedMember]?.liabilities || 0
                : data?.summary.total_liabilities || 0
            )}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <p className="text-sm text-stone">Net Worth</p>
          <p className={`text-2xl font-bold tabular-nums ${
            (selectedMember
              ? data?.summary.by_family_member[selectedMember]?.net_worth || 0
              : data?.summary.net_worth || 0) >= 0
              ? 'text-forest-dark'
              : 'text-danger'
          }`}>
            {loading ? '...' : formatCurrency(
              selectedMember
                ? data?.summary.by_family_member[selectedMember]?.net_worth || 0
                : data?.summary.net_worth || 0
            )}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone/20">
        <div className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-forest text-forest-dark'
                  : 'border-transparent text-stone hover:text-charcoal'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Net Worth Projections */}
          {!selectedMember && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <h2 className="text-lg font-semibold text-charcoal mb-4">Net Worth Projections</h2>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-sm text-stone mb-1">1 Year</p>
                  <p className={`text-xl font-bold tabular-nums ${
                    (data?.summary.projections.one_year || 0) >= 0 ? 'text-forest-dark' : 'text-danger'
                  }`}>
                    {loading ? '...' : formatCurrency(data?.summary.projections.one_year || 0)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-stone mb-1">5 Years</p>
                  <p className={`text-xl font-bold tabular-nums ${
                    (data?.summary.projections.five_year || 0) >= 0 ? 'text-forest-dark' : 'text-danger'
                  }`}>
                    {loading ? '...' : formatCurrency(data?.summary.projections.five_year || 0)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-stone mb-1">10 Years</p>
                  <p className={`text-xl font-bold tabular-nums ${
                    (data?.summary.projections.ten_year || 0) >= 0 ? 'text-forest-dark' : 'text-danger'
                  }`}>
                    {loading ? '...' : formatCurrency(data?.summary.projections.ten_year || 0)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Asset Breakdown */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <h2 className="text-lg font-semibold text-charcoal mb-4">Assets by Type</h2>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-pulse text-sage">Loading...</div>
                </div>
              ) : assetTypeData.length > 0 ? (
                <DonutChart
                  data={assetTypeData}
                  centerLabel="Total"
                  centerValue={formatCurrency(
                    selectedMember
                      ? data?.summary.by_family_member[selectedMember]?.assets || 0
                      : data?.summary.total_assets || 0
                  )}
                  height={280}
                />
              ) : (
                <div className="text-center py-12 text-sage">No assets yet</div>
              )}
            </div>

            {/* Debt Breakdown */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <h2 className="text-lg font-semibold text-charcoal mb-4">Liabilities by Type</h2>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-pulse text-sage">Loading...</div>
                </div>
              ) : debtTypeData.length > 0 ? (
                <DonutChart
                  data={debtTypeData}
                  centerLabel="Total"
                  centerValue={formatCurrency(
                    selectedMember
                      ? data?.summary.by_family_member[selectedMember]?.liabilities || 0
                      : data?.summary.total_liabilities || 0
                  )}
                  height={280}
                />
              ) : (
                <div className="text-center py-12 text-sage">No liabilities yet</div>
              )}
            </div>
          </div>

          {/* Family Member Comparison */}
          {!selectedMember && familyMemberData.length > 1 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <h2 className="text-lg font-semibold text-charcoal mb-4">By Family Member</h2>
              <BarChart
                data={familyMemberData}
                label1="Assets"
                label2="Liabilities"
                layout="horizontal"
                height={250}
                showLegend
              />
            </div>
          )}
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-sage">Loading assets...</div>
          ) : filteredAssets.length > 0 ? (
            filteredAssets.map(asset => (
              <div
                key={asset.id}
                className="bg-white rounded-xl shadow-sm p-5 border border-stone/10"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-charcoal">{asset.name}</h3>
                      {asset.ticker && (
                        <span className="text-xs px-2 py-0.5 bg-forest/10 text-forest rounded-full">
                          {asset.ticker}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-stone mt-1">
                      {assetTypeLabels[asset.type] || asset.type} • {asset.family_member}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-forest-dark tabular-nums">
                      {formatCurrency(asset.current_value)}
                    </p>
                    {asset.gain_loss !== null && (
                      <p className={`text-sm tabular-nums ${asset.gain_loss >= 0 ? 'text-success' : 'text-danger'}`}>
                        {asset.gain_loss >= 0 ? '+' : ''}{formatCurrency(asset.gain_loss)}
                        {asset.gain_loss_percent !== null && (
                          <span className="ml-1">
                            ({asset.gain_loss_percent >= 0 ? '+' : ''}{asset.gain_loss_percent.toFixed(1)}%)
                          </span>
                        )}
                      </p>
                    )}
                    {asset.appreciation_rate > 0 && (
                      <p className="text-xs text-stone mt-1">
                        {(asset.appreciation_rate * 100).toFixed(1)}% expected growth
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <div className="text-center py-8">
                <div className="text-4xl mb-3">💰</div>
                <h3 className="font-semibold text-charcoal">No assets yet</h3>
                <p className="text-sm text-sage mt-1">
                  Add your first asset to start tracking your net worth
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'liabilities' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-sage">Loading liabilities...</div>
          ) : filteredDebts.length > 0 ? (
            filteredDebts.map(debt => (
              <div
                key={debt.id}
                className="bg-white rounded-xl shadow-sm p-5 border border-stone/10"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-charcoal">{debt.name}</h3>
                    <p className="text-sm text-stone mt-1">
                      {debtTypeLabels[debt.type] || debt.type} • {debt.family_member}
                    </p>
                    {debt.payoff_date && (
                      <p className="text-sm text-sage mt-2">
                        Payoff: {new Date(debt.payoff_date).toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric'
                        })}
                        {debt.months_remaining && (
                          <span className="ml-1 text-stone">
                            ({debt.months_remaining} months)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-danger tabular-nums">
                      {formatCurrency(debt.principal)}
                    </p>
                    <p className="text-sm text-stone tabular-nums">
                      {debt.interest_rate.toFixed(2)}% APR
                    </p>
                    <p className="text-sm text-charcoal tabular-nums mt-1">
                      {formatCurrency(debt.monthly_payment)}/mo
                    </p>
                  </div>
                </div>

                {/* Progress bar showing amount paid off */}
                {debt.months_remaining && debt.months_remaining > 0 && (
                  <div className="mt-4">
                    <div className="h-2 bg-stone/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-success rounded-full transition-all"
                        style={{
                          width: `${Math.max(0, Math.min(100, 100 - (debt.months_remaining / 360) * 100))}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <div className="text-center py-8">
                <div className="text-4xl mb-3">🎉</div>
                <h3 className="font-semibold text-charcoal">No liabilities</h3>
                <p className="text-sm text-sage mt-1">
                  You're debt-free! Keep up the great work.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
